import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message, Button, theme } from 'antd';

import SkuItemsFilter from './components/SkuItemsFilter';
import SkuItemsList from './components/SkuItemsList';
import SyncSkuItemsModal from './components/SyncSkuItemsModal';
import { fetchSkuItems, applyFilters, syncAmazonSkuItems } from './actions';
import './SkuItems.css';

const SkuItems = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    const {
        skuItems,
        loading,
        pagination,
        filters: { searchText, selectedFulfillmentChannel, selectedStatus, selectedSellerAccount }
    } = useSelector((state) => state.skuItems);

    const [syncModalVisible, setSyncModalVisible] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);

    useEffect(() => {
        const initialSearchText = searchParams.get('searchText');
        if (initialSearchText) {
            dispatch(applyFilters(initialSearchText, selectedFulfillmentChannel, selectedStatus, selectedSellerAccount));
            return;
        }
        dispatch(fetchSkuItems(1, pagination.pageSize));
    }, [dispatch]);

    const handleSearch = (value) => {
        dispatch(applyFilters(value, selectedFulfillmentChannel, selectedStatus, selectedSellerAccount));
    };

    const handleFulfillmentChannelChange = (value) => {
        dispatch(applyFilters(searchText, value, selectedStatus, selectedSellerAccount));
    };

    const handleStatusChange = (value) => {
        dispatch(applyFilters(searchText, selectedFulfillmentChannel, value, selectedSellerAccount));
    };

    const handleSellerAccountChange = (value) => {
        dispatch(applyFilters(searchText, selectedFulfillmentChannel, selectedStatus, value));
    };

    const handleApplyAllFilters = (filters) => {
        dispatch(applyFilters(filters.searchText, filters.selectedFulfillmentChannel, filters.selectedStatus, filters.selectedSellerAccount));
    };

    const handleSyncClick = () => {
        setSyncModalVisible(true);
    };

    const handleSyncOk = async (syncData) => {
        try {
            setSyncLoading(true);

            if (!syncData.selectedIds || syncData.selectedIds.length === 0) {
                message.error('Vui lòng chọn ít nhất một Seller Account');
                return;
            }

            const requestData = {
                sellerAccountIds: syncData.selectedIds,
                type: 'ALL'
            };

            // Thêm date filters nếu có
            if (syncData.createdAfter && syncData.createdBefore) {
                requestData.createdAfter = syncData.createdAfter;
                requestData.createdBefore = syncData.createdBefore;
            } else if (syncData.lastUpdatedAfter && syncData.lastUpdatedBefore) {
                requestData.lastUpdatedAfter = syncData.lastUpdatedAfter;
                requestData.lastUpdatedBefore = syncData.lastUpdatedBefore;
            }

            const result = await dispatch(syncAmazonSkuItems(requestData));

            if (result.success) {
                message.success(result.message);
                setSyncModalVisible(false);
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.error('Error syncing SKU items:', error);
            message.error('Có lỗi xảy ra khi đồng bộ SKU từ Amazon');
        } finally {
            setSyncLoading(false);
        }
    };

    const handleSyncCancel = () => {
        setSyncModalVisible(false);
    };

    // Memoize style objects to prevent re-creation
    const mainContainerStyle = useMemo(() => ({
        margin: '0px 10px 10px 10px',
        padding: '10px 16px 16px 16px',
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
        minHeight: 280,
    }), [colorBgContainer, borderRadiusLG]);

    return (
        <div>
            <div
                style={{
                    margin: '10px 16px 0px 16px',
                }}
            >
                <SkuItemsFilter
                    onSearch={handleSearch}
                    onFulfillmentChannelChange={handleFulfillmentChannelChange}
                    onStatusChange={handleStatusChange}
                    onSellerAccountChange={handleSellerAccountChange}
                    onApplyAllFilters={handleApplyAllFilters}
                    searchText={searchText}
                    selectedFulfillmentChannel={selectedFulfillmentChannel}
                    selectedStatus={selectedStatus}
                    selectedSellerAccount={selectedSellerAccount}
                    loading={loading}
                />
            </div>
            <div style={mainContainerStyle}>
                <SkuItemsList
                    skuItems={skuItems}
                    isLoading={loading}
                    pagination={pagination}
                    onSyncClick={handleSyncClick}
                />
            </div>
            <SyncSkuItemsModal
                visible={syncModalVisible}
                onOk={handleSyncOk}
                onCancel={handleSyncCancel}
                loading={syncLoading}
            />
        </div>
    );
};

export default SkuItems;
